import { AdminGameSelector } from "@/components/classic-admin/AdminGameSelector";
import { adminGameFromLocation } from "@/lib/admin-game-api";
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Mail, Newspaper, Trophy, Ticket, Gift, LogOut, RotateCcw, Settings2, Shield, SlidersHorizontal, Users, UserSearch, ChevronDown, History, UserCog, AppWindow } from "lucide-react";
import { logout } from "@/lib/api";
import { useAuthStore } from "@/lib/auth-store";
import { useDirtyStore } from "@/lib/dirty-store";
import { cn } from "@/lib/utils";
import { initials } from "@/components/players/player-presenters";

// Shared by every page in the soft-charcoal workspace. Styles live in players-workspace.css.
const links = [
  { to: "/accounts", label: "Players", icon: Users },
  { to: "/purchases", label: "Purchases", icon: Gift },
  { to: "/flags", label: "Moderation", icon: Shield },
  { to: "/account-recovery", label: "Recovery", icon: RotateCcw },
];

// Foldout state survives page changes and refreshes; storage may be unavailable (private mode).
function readOpen(id: string) {
  try { return localStorage.getItem(`ec2-nav-open:${id}`) === "1"; } catch { return false; }
}

function NavGroup({ id, icon, label, children }: { id: string; icon: React.ReactNode; label: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(() => readOpen(id));
  function toggle(event: React.SyntheticEvent<HTMLDetailsElement>) {
    const next = event.currentTarget.open;
    setOpen(next);
    try { localStorage.setItem(`ec2-nav-open:${id}`, next ? "1" : "0"); } catch { /* not persisted */ }
  }
  return <details className="pw-nav-group" open={open} onToggle={toggle}><summary>{icon}<span>{label}</span><ChevronDown size={14} /></summary>
    {children}
  </details>;
}

export function WorkspaceNavigation({ onNavigate }: { onNavigate?: () => void }) {
  function navigate(event: React.MouseEvent<HTMLAnchorElement>) {
    if (!useDirtyStore.getState().confirmLeave()) { event.preventDefault(); return; }
    onNavigate?.();
  }
  const me = useAuthStore(s => s.me);
  const { pathname, search } = useLocation();
  const game = adminGameFromLocation(pathname, search);
  const active = (to: string) => pathname === to || pathname.startsWith(`${to}/`);
  return <>
    <AdminGameSelector onNavigate={onNavigate} />
    <nav aria-label="Main navigation" className="pw-navigation">
      <p className="pw-nav-label">WORKSPACE</p>
      {game === 'ec1' ? [
        { to: '/games/ec1/players', label: 'Players', icon: Users },
        { to: '/games/ec1/legacy', label: 'PlayFab (legacy)', icon: UserSearch },
        ...(me?.role === 'internal' ? [{ to: '/games/ec1/purchases', label: 'Purchases', icon: Gift }] : []),
        { to: '/games/ec1/coupons', label: 'Coupons', icon: Ticket },
        { to: '/games/ec1/mail', label: 'Player mail', icon: Mail },
        { to: '/games/ec1/news', label: 'News', icon: Newspaper },
        { to: '/games/ec1/leaderboards', label: 'Leaderboards', icon: Trophy },
        { to: '/games/ec1/audit', label: 'Audit logs', icon: History },
        { to: '/games/ec1/status', label: 'Service status', icon: AppWindow },
        { to: '/admins?game=ec1', label: 'Admins', icon: UserCog },
      ].map(({ to, label, icon: Icon }) => <Link key={to} to={to} onClick={navigate} className={cn('pw-nav-link', active(to.split('?')[0]) && 'is-active')} aria-current={active(to.split('?')[0]) ? 'page' : undefined}><Icon size={18} strokeWidth={1.6} /><span>{label}</span></Link>) : <>
      {links.filter(({ to }) => to !== "/purchases" || me?.role === "internal").map(({ to, label, icon: Icon }) => <Link key={to} to={to} onClick={navigate}
        className={cn("pw-nav-link", active(to) && "is-active")} aria-current={active(to) ? "page" : undefined}>
        <Icon size={18} strokeWidth={1.6} /><span>{label}</span>
      </Link>)}
      <NavGroup id="game-configuration" icon={<Settings2 size={18} strokeWidth={1.6} />} label="Game configuration">
        {[ ["/game-versions", "Game versions"], ["/balance", "Balance"], ["/trial", "Chamber of Trial"], ["/events", "Events"], ["/news", "News"], ["/coupons", "Coupons"], ["/raffle", "Raffle"] ].map(([to, label]) => <Link key={to} to={to} onClick={navigate}
          className={cn(active(to) && "is-active")} aria-current={active(to) ? "page" : undefined}>{label}</Link>)}
      </NavGroup>
      <NavGroup id="administration" icon={<SlidersHorizontal size={18} strokeWidth={1.6} />} label="Administration">
        {[ ["/audit", "Audit logs"], ["/admins", "Admins"], ["/dev-accounts", "Developer accounts"], ["/players", "PlayFab (legacy)"] ].map(([to, label]) => <Link key={to} to={to} onClick={navigate}
          className={cn(active(to) && "is-active")} aria-current={active(to) ? "page" : undefined}>{label}</Link>)}
      </NavGroup>
      </>}
    </nav>
    <div className="pw-admin"><span className="pw-avatar pw-avatar-small">{initials(me?.globalName ?? me?.username)}</span><div><strong>{me?.globalName ?? me?.username ?? "Admin"}</strong><span>{game === 'ec1' ? 'Epic Conquest Classic' : 'Epic Conquest 2'}</span></div>
      <button className="pw-icon-button" aria-label="Sign out" onClick={() => { void logout().then(() => { useAuthStore.getState().clear(); window.location.assign("/login"); }); }}><LogOut size={16} /></button>
    </div>
  </>;
}
